import { Pill } from '@els/els-react--pill';
import { TimeSpentRS } from 'models/api-response';
import { AssessmentStatus, AssignmentType, GradingOption, GradingResult } from 'models/enum';
import { AnswerSubmission, CaseStudy, CaseStudySubmissionSummary, ChartComment, SimulationEhr } from 'models/ui';
import { Locales } from 'constants/app.constant';
import { appHelper, assignmentHelper } from 'helpers';
import { formatDate, toMomentWithParsers } from 'helpers/datetime.helper';
import {
  AllowEvent,
  AssessmentStatistic,
  Badge,
  Box,
  ButtonLinkIcon,
  ChartCommentList,
  ELSTextarea,
  InfoText,
  NavigationComponent,
  OverlayBlock,
  WarningCard
} from 'components/common';
import { ELSButton, ELSDropDown, ELSFlex, ELSFlexItem, ELSIcon, ELSTab, ELSTabGroup, ELSTooltip } from 'components/common/els';
import ScoreGrade from 'components/features/assignment-submission/ScoreGrade';
import { FormFieldLabel } from './constants';
import { StudentSubmission, Tabs } from './AssessmentDetail';
import ContentInformation from './ContentInformation';
import './assessment.detail.scss';
import '../assignment/create-assignment-workflow/case-study-content/case.study.content.scss';
import { QuizQuestionsResult } from '../quiz-questions-result/QuizQuestionsResult';

export interface AssignmentDetailViewProps {
  title: string;
  assignmentType: AssignmentType;
  assignmentGradeType: GradingOption;
  submissions: StudentSubmission[];
  startedAt: string;
  completedAt: string;
  durationTime: number;
  score?: number;
  status: string;
  gradeStatus: GradingResult;
  selectedStudent: number;
  completedCharts: string[];
  chartComments: ChartComment[];
  instructorSetUpChartComments?: ChartComment[];
  overallComment: string;
  overallCommentCreatedAt?: string;
  overallCommentInProgress?: string;
  selectedContent: CaseStudy | SimulationEhr;
  caseStudySubmissionSummary: CaseStudySubmissionSummary;
  questions: AnswerSubmission[];
  activeTab: Tabs;
  studentNavigationIndex: number;
  warningMessage: string;
  onBackToDetail: VoidFunction;
  onViewChart: Function;
  onStudentChange: Function;
  onTabsClick: Function;
  onViewSpecificChart: Function;
  onOverallCommentChange: Function;
  onSaveClick: Function;
  onPrevStudentClick: Function;
  onNextStudentClick: Function;
  timeSpentData: TimeSpentRS;
  assessmentId: number;
  locale: Locales;
  isInProgressGradingEnabled: boolean;
}

export const AssessmentDetailView = (props: AssignmentDetailViewProps) => {
  const {
    assignmentType,
    assignmentGradeType,
    gradeStatus,
    score,
    title,
    startedAt,
    completedAt,
    durationTime,
    completedCharts,
    selectedStudent,
    submissions,
    chartComments,
    instructorSetUpChartComments,
    status,
    overallComment,
    overallCommentCreatedAt,
    overallCommentInProgress,
    questions,
    caseStudySubmissionSummary,
    selectedContent,
    studentNavigationIndex,
    warningMessage,
    assessmentId,
    locale,
    isInProgressGradingEnabled,
    onBackToDetail,
    onViewSpecificChart,
    onOverallCommentChange,
    onSaveClick,
    onStudentChange,
    onPrevStudentClick,
    onNextStudentClick,
    timeSpentData
  } = props;

  const quizQuestionsCorrect = caseStudySubmissionSummary?.totalCorrect;
  const totalIncorrectQuestions = isInProgressGradingEnabled
    ? assignmentHelper.getIncorrectQuestions(caseStudySubmissionSummary?.sequenceChunkSummaries)
    : caseStudySubmissionSummary?.totalQuestions - quizQuestionsCorrect;
  const isAnyPhaseCompleted = assignmentHelper.checkIfAnyPhaseCompleted(caseStudySubmissionSummary?.sequenceChunkSummaries);
  const studentList = submissions.map((item) => ({ value: String(item.studentId), name: item.displayName }));
  const showOverlayBlock = (isInProgressGradingEnabled && !startedAt) || (!isInProgressGradingEnabled && !assignmentHelper.isAssessmentSubmitted(status));
  const showOverallCommentsPill = isInProgressGradingEnabled && (status === AssessmentStatus.IN_PROGRESS || status === AssessmentStatus.PAST_DUE) && overallComment;
  const hasAnyCharts = completedCharts.length > 0 || (instructorSetUpChartComments && instructorSetUpChartComments.length > 0);

  return (
    <div className="assessment-detail">
      <div className="assessment-detail__header">
        <Box py>
          <ButtonLinkIcon className="u-els-font-size-meta" text="Back to Assignment Detail" iconName="chevron-left" iconSize="3o4" onClick={onBackToDetail} />
        </Box>
        <div className="jumbotron-name">
          <ELSFlex middle spaceBetween>
            <ELSFlexItem className="u-els-font-size-h1">{title}</ELSFlexItem>
            <ELSFlexItem>
              <ELSTooltip
                wrapperClassName={`tooltip-overlay-header ${hasAnyCharts ? 'u-els-display-none' : ''}`}
                content="View EHR Charts is disabled because no charts were filled out"
                showWhenHover
                showClose
              >
                {!hasAnyCharts ? (
                  <AllowEvent>
                    <ELSButton text="View EHR Charts" isDisabled onClick={props.onViewChart} />
                  </AllowEvent>
                ) : (
                  <ELSButton text="View EHR Charts" onClick={props.onViewChart} />
                )}
              </ELSTooltip>
            </ELSFlexItem>
          </ELSFlex>
        </div>
        <ELSFlex className="student-selection" middle left>
          <ELSFlexItem className="u-els-padding-right">
            <ELSIcon name="users" align="middle" />
          </ELSFlexItem>
          <ELSFlexItem>
            <ELSDropDown
              id="student-options"
              ariaLabel="Select Students"
              name="student-options"
              value={`${selectedStudent}`}
              options={studentList}
              inline
              size="1x1o2"
              changeHandler={onStudentChange}
            />
          </ELSFlexItem>
          <ELSFlexItem>
            <Badge inline className="pending-grade">
              {assignmentHelper.getPrettyStatus(status as AssessmentStatus)}
            </Badge>
          </ELSFlexItem>
          {!assignmentHelper.isAssessmentSubmitted(status) && (
            <ELSFlexItem className="u-els-padding-left">
              <WarningCard message={warningMessage} />
            </ELSFlexItem>
          )}
        </ELSFlex>
      </div>
      <div className="assessment-detail__info">
        <div>
          <AssessmentStatistic
            gradeType={assignmentGradeType}
            gradeStatus={gradeStatus}
            score={assignmentHelper.getPrettyScore(score)}
            startedAt={startedAt}
            completedAt={completedAt}
            durationTime={durationTime}
            completedChartsLength={completedCharts.length}
            assessmentId={assessmentId}
            timeSpentData={timeSpentData}
            locale={locale}
            isInProgressGradingEnabled={isInProgressGradingEnabled}
          />
          <InfoText
            text={`Any EHR chart that the student has completed is listed here.
       You can leave comments on either individual charts, or make overall comments.
       Please note that a student can view any feedback you provide as soon as you save.`}
          />
          <Box ml3 mb2 className="u-text-nowrap u-els-margin-top-1x1o2">
            {AssignmentType.EMPTY_EHR === assignmentType && (
              <ELSTabGroup activeIndex={props.activeTab} onChangeActiveIndex={props.onTabsClick}>
                <ELSTab title="Chart Activity" />
              </ELSTabGroup>
            )}
            {AssignmentType.CASE_STUDY === assignmentType && (
              <ELSTabGroup activeIndex={props.activeTab} onChangeActiveIndex={props.onTabsClick}>
                <ELSTab title="Chart Activity" />
                <ELSTab title="Quiz Questions" className="u-els-margin-top-1x1o2" isDisabled={isInProgressGradingEnabled && !isAnyPhaseCompleted} />
                <ELSTab title="Case Study Information" />
              </ELSTabGroup>
            )}

            {AssignmentType.SIMULATION === assignmentType && (
              <ELSTabGroup activeIndex={props.activeTab} onChangeActiveIndex={props.onTabsClick}>
                <ELSTab title="Chart Activity" />
                <ELSTab title="Simulation EHR Information" />
              </ELSTabGroup>
            )}
          </Box>
          {props.activeTab === Tabs.CHART_ACTIVITY && (
            <Box ml>
              <ChartCommentList
                className="u-els-margin-top-1x1o2 u-els-padding-left-2x u-els-padding-right-2x"
                chartComments={chartComments}
                instructorSetUpChartComments={instructorSetUpChartComments}
                onViewChartClick={onViewSpecificChart}
                timeSpent={timeSpentData?.charts}
                locale={locale}
              />
              <div className="assessment-detail__overall-comments">
                {showOverallCommentsPill && (
                  <>
                    <h2>Overall Comments</h2>
                    <div className="u-els-display-flex u-els-margin-bottom-1x1o2 assessment-detail__overall-comments--align-center">
                      <Pill
                        label={formatDate({
                          date: toMomentWithParsers(overallCommentCreatedAt).toDate(),
                          locale,
                          includeTime: true
                        })}
                        className="u-els-color-black u-els-background-color-n6 u-els-margin-right u-els-height-1o1"
                      />
                      <p className="assignment-submission__overall-comments">{overallComment}</p>
                    </div>
                  </>
                )}
                <ELSTextarea
                  textLabel={showOverallCommentsPill ? '' : FormFieldLabel.OVERALL_COMMENTS}
                  name="overall-comments-textarea"
                  className="overall-comments__textarea"
                  rows={5}
                  placeholder={appHelper.getPlaceholder(FormFieldLabel.OVERALL_COMMENTS)}
                  value={showOverallCommentsPill ? overallCommentInProgress : overallComment}
                  onChange={onOverallCommentChange}
                  aria-label={FormFieldLabel.OVERALL_COMMENTS}
                />
              </div>
              <ELSFlex className="assessment-detail__footer-actions u-els-margin-top">
                <ELSFlexItem grow>
                  <ELSButton type="secondary" className="sng-button" text="Save" onClick={onSaveClick} />
                </ELSFlexItem>
                <ELSFlexItem className="assessment-detail__prev-next-student">
                  <NavigationComponent
                    title=""
                    current={studentNavigationIndex}
                    total={studentList.length}
                    prevText="Previous student"
                    onPrev={onPrevStudentClick}
                    nextText="Next student"
                    onNext={onNextStudentClick}
                  />
                </ELSFlexItem>
              </ELSFlex>
            </Box>
          )}
          {props.activeTab === Tabs.QUIZ_QUESTIONS && assignmentType !== AssignmentType.SIMULATION && (
            <>
              {assignmentGradeType === GradingOption.SCORED && (
                <Box py2 px3>
                  <ScoreGrade className="u-els-width-1o2 u-els-width-1o1@tablet" score={score} assessmentStatus={status} isCompleted />
                </Box>
              )}
              <QuizQuestionsResult questions={questions} totalCorrect={quizQuestionsCorrect} totalIncorrect={totalIncorrectQuestions} />
            </>
          )}
          {(props.activeTab === Tabs.CASE_STUDY_INFORMATION || (assignmentType === AssignmentType.SIMULATION && props.activeTab === Tabs.SIMULATION_EHR_INFORMATION)) && (
            <Box className="case-study-content" px3 pb2>
              <ContentInformation selectedContent={selectedContent} assignmentType={assignmentType} />
            </Box>
          )}
        </div>
        {showOverlayBlock && <OverlayBlock />}
      </div>
    </div>
  );
};

AssessmentDetailView.displayName = 'AssessmentDetailView';
export default AssessmentDetailView;
